// src/components/VerificationMessage.jsx
import React from 'react';
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import { useToast } from "./ui/use-toast";
import { supabase } from '../lib/supabase';
import { Mail, AlertTriangle, CheckCircle } from "lucide-react";
import { useState, useEffect } from 'react';

export const VerificationMessage = () => {
  const [sending, setSending] = useState(false);
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isNewSignUp, setIsNewSignUp] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check for verification info in localStorage
    const verificationNeeded = localStorage.getItem('verificationNeeded') === 'true';
    
    if (verificationNeeded) {
      const storedEmail = localStorage.getItem('verificationEmail') || '';
      const newSignUp = localStorage.getItem('isNewSignUp') === 'true';
      const timestamp = parseInt(localStorage.getItem('verificationTimestamp') || '0', 10);
      
      // Only show the message if it's less than 1 hour old (prevents stale messages)
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      if (timestamp > oneHourAgo) {
        setVisible(true);
        setEmail(storedEmail);
        setIsNewSignUp(newSignUp);
      } else {
        // Clear old verification info
        clearVerificationInfo();
      }
    }
  }, []);

  const clearVerificationInfo = () => {
    localStorage.removeItem('verificationNeeded');
    localStorage.removeItem('verificationEmail');
    localStorage.removeItem('isNewSignUp');
    localStorage.removeItem('verificationTimestamp');
  };

  const handleDismiss = () => {
    setVisible(false);
    clearVerificationInfo();
  };

  const handleResendVerification = async () => {
    if (!email || sending) return;
    
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });
      
      if (error) throw error;
      
      toast({
        title: "Verification Email Sent",
        description: `Check your inbox at ${email} for the verification link.`,
        duration: 5000
      });
    } catch (error) {
      console.error('Error resending verification:', error);
      toast({
        title: "Error Sending Email",
        description: error.message || "Failed to send verification email. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  if (!visible) return null;

  return (
    <Alert variant={isNewSignUp ? "default" : "destructive"} className="mb-6 relative">
      {isNewSignUp 
        ? <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
        : <AlertTriangle className="h-4 w-4 mr-2" />
      }
      <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2 pr-8">
        <div className="flex-1">
          {isNewSignUp 
            ? <strong>Account created successfully!</strong> 
            : <strong>Email Verification Required:</strong>
          } Please check your inbox and verify your email address to unlock full functionality.
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleResendVerification}
          disabled={sending}
          className="whitespace-nowrap"
        >
          <Mail className="h-4 w-4 mr-2" />
          {sending ? "Sending..." : "Resend Email"}
        </Button>
      </AlertDescription>
      <Button 
        variant="ghost" 
        size="sm" 
        className="absolute top-2 right-2 h-6 w-6 p-0 rounded-full"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        ✕
      </Button>
    </Alert>
  );
};